import { STREAM_PATH } from '@pm2-monitor/shared';
import { readToken } from '../../api/connection';
import { useStore } from '../../state/store';
import { formatClock, formatDuration, formatGiB, formatUptime } from '../../lib/format';
import { DefRow, Heading, Panel, Placeholder, Sub } from '../ui';

export function Server() {
  const system = useStore((s) => s.system);
  const streamStatus = useStore((s) => s.streamStatus);
  const statusIntervalMs = useStore((s) => s.statusIntervalMs);
  const connectedSince = useStore((s) => s.connectedSince);
  const sampleCount = useStore((s) => s.sampleCpu.length);
  const now = useStore((s) => s.now);
  const scope = readToken()?.scope ?? 'session';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))',
        gap: 16,
        alignItems: 'start',
      }}
    >
      <Panel style={{ padding: '22px 24px' }}>
        <Heading style={{ marginBottom: 14 }}>Host</Heading>
        {system === null ? (
          <Placeholder title="Waiting for /system" />
        ) : (
          <>
            <DefRow label="hostname" value={system.hostname} />
            <DefRow label="platform" value={`${system.platform} ${system.arch}`} />
            <DefRow label="node" value={system.nodeVersion} />
            <DefRow label="pm2" value={system.pm2Version} />
            <DefRow label="cpu" value={`${system.cpuCount} cores`} />
            <DefRow label="cpu model" value={system.cpuModel} />
            <DefRow
              label="memory"
              value={`${formatGiB(system.totalMemory - system.freeMemory)} / ${formatGiB(system.totalMemory)}`}
            />
            <DefRow
              label="load"
              value={system.loadavg.map((n) => n.toFixed(2)).join(' · ')}
            />
            <DefRow label="os uptime" value={formatDuration(system.uptime)} />
            <DefRow label="bridge up" value={formatUptime(system.bridgeStartedAt, now)} />
            <DefRow label="bridge" value={system.bridgeVersion} />
          </>
        )}
      </Panel>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Panel style={{ padding: '22px 24px' }}>
          <Heading style={{ marginBottom: 14 }}>Connection</Heading>
          <DefRow label="endpoint" value={window.location.host} />
          <DefRow
            label="transport"
            value={`${window.location.protocol.replace(':', '')} · same-origin /api/v1`}
          />
          <DefRow
            label="stream"
            value={`${STREAM_PATH} · ${
              streamStatus === 'open'
                ? `1 frame / ${(statusIntervalMs / 1000).toFixed(statusIntervalMs % 1000 === 0 ? 0 : 1)}s`
                : streamStatus
            }`}
          />
          <DefRow
            label="connected"
            value={
              connectedSince === null
                ? '—'
                : `${formatUptime(connectedSince, now)} · since ${formatClock(connectedSince)}`
            }
          />
          <DefRow
            label="auth"
            value={`Bearer · ${scope === 'device' ? 'stored on this device' : 'this tab only'}`}
          />
          <DefRow label="buffered" value={`${sampleCount} samples`} />

          <div
            style={{
              fontSize: 12.5,
              color: 'var(--faint)',
              marginTop: 14,
              maxWidth: '58ch',
              lineHeight: 1.55,
              textWrap: 'pretty',
            }}
          >
            Environment variables are redacted default-deny by the bridge: only an
            explicit allowlist of operational keys is revealed, and everything else is
            masked before it leaves the host. Every mutating call is appended to the audit
            log, including failures.
          </div>
        </Panel>

        <Panel style={{ padding: '22px 24px' }}>
          <Heading>What this console can and cannot do</Heading>
          <Sub style={{ marginBottom: 8 }}>The phone app covers the rest</Sub>
          <Note tone="pos">
            Reads every process pm2 manages: status, cpu, memory, restarts, uptime
          </Note>
          <Note tone="pos">
            Live status frames and merged log output over the WebSocket
          </Note>
          <Note tone="pos">
            Acknowledges alerts and arms or disarms rules, shared with the app
          </Note>
          <Note tone="neg">
            No lifecycle control — restart, reload, stop, start and delete are not wired
            here, and the client has no code path that could call them
          </Note>
          <Note tone="neg">
            Metrics are in memory: the bridge keeps a 60-second window, so 15m and 1h
            accumulate in this tab and reset when it closes
          </Note>
          <Note tone="neg">
            The bearer token is held in the browser and grants read access to every
            process on the host — "Forget token" in the sidebar clears it
          </Note>
        </Panel>
      </div>
    </div>
  );
}

function Note({ tone, children }: { tone: 'pos' | 'neg'; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 11,
        padding: '10px 0',
        borderTop: '1px solid var(--line-soft)',
        fontSize: 13,
      }}
    >
      <div
        style={{
          width: 6,
          height: 6,
          flex: 'none',
          borderRadius: 999,
          background: tone === 'pos' ? 'var(--pos)' : 'var(--neg)',
          marginTop: 7,
        }}
      />
      <div style={{ color: 'var(--muted)', lineHeight: 1.55, textWrap: 'pretty' }}>
        {children}
      </div>
    </div>
  );
}
